export const currencyFormatter = new Intl.NumberFormat("bg-BG", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 3,
});

export const percentFormatter = new Intl.NumberFormat("bg-BG", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

/** Дял % без закръгляне до цяло число (2 знака след десетичната). */
export const sharePercentFormatter = new Intl.NumberFormat("bg-BG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

export function formatSharePercent(part: number, total: number): string {
    if (total <= 0) {
        return sharePercentFormatter.format(0);
    }

    return sharePercentFormatter.format((part / total) * 100);
}

/** POS: 1 касова бележка / N касови бележки */
export function formatPosReceiptsLabel(count: number): string {
    const n = Math.abs(Math.trunc(count));
    const formatted = n.toLocaleString("bg-BG");

    return n === 1 ? "1 касова бележка" : `${formatted} касови бележки`;
}

const soldQtyFormatter = new Intl.NumberFormat("bg-BG", {
    maximumFractionDigits: 2,
});

/** Продадени бройки: „1 234 бр.“ */
export function formatSoldQuantityLabel(quantity: number): string {
    return `${soldQtyFormatter.format(quantity)} бр.`;
}

const BUSINESS_TIMEZONE = "Europe/Sofia";

export function formatMessageDateTime(value: string | null | undefined): string {
    const raw = (value ?? "").trim();

    if (raw === "") {
        return "";
    }

    const parsedValue = raw.includes("T") ? raw : `${raw.replace(" ", "T")}Z`;

    try {
        const date = new Date(parsedValue);

        if (Number.isNaN(date.getTime())) {
            return raw;
        }

        return date.toLocaleString("bg-BG", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            timeZone: BUSINESS_TIMEZONE,
        });
    } catch {
        return raw;
    }
}

export function formatChannelLabel(channel: string) {
    switch (channel) {
        case "WooCommerce":
            return "Онлайн";
        case "Expo":
            return "Търговия на едро";
        case "POS":
            return "Магазин";
        default:
            return channel;
    }
}

export function formatSourceLabel(source: string) {
    switch (source) {
        case "Partners":
            return "Партньори";
        default:
            return source;
    }
}

export function getCustomerStatusChipStyles(status: string) {
    switch (status) {
        case "Свързан":
            return { bgcolor: "rgba(15, 118, 110, 0.12)", color: "#0f766e" };
        case "Частичен":
            return { bgcolor: "rgba(217, 119, 6, 0.14)", color: "#b45309" };
        default:
            return { bgcolor: "rgba(220, 38, 38, 0.12)", color: "#b91c1c" };
    }
}

export function getConsentChipStyles(consent: string) {
    if (consent === "Липсва") {
        return { bgcolor: "rgba(220, 38, 38, 0.12)", color: "#b91c1c" };
    }

    if (consent.includes("SMS") || consent.includes("Viber")) {
        return { bgcolor: "rgba(2, 132, 199, 0.12)", color: "#0369a1" };
    }

    return { bgcolor: "rgba(15, 118, 110, 0.12)", color: "#0f766e" };
}

export function getSystemStatusChipStyles(status: string) {
    switch (status) {
        case "Стабилна":
            return { bgcolor: "rgba(15, 118, 110, 0.12)", color: "#0f766e" };
        case "Внимание":
            return { bgcolor: "rgba(217, 119, 6, 0.14)", color: "#b45309" };
        default:
            return { bgcolor: "rgba(71, 85, 105, 0.12)", color: "#475569" };
    }
}

export function getSeverityChipStyles(severity: string) {
    switch (severity) {
        case "Високо":
            return { bgcolor: "rgba(220, 38, 38, 0.12)", color: "#b91c1c" };
        case "Средно":
            return { bgcolor: "rgba(217, 119, 6, 0.14)", color: "#b45309" };
        default:
            return { bgcolor: "rgba(15, 118, 110, 0.12)", color: "#0f766e" };
    }
}

export function getOrderStatusChipStyles(status: string) {
    switch (status) {
        case "Нова":
            return { bgcolor: "rgba(59, 130, 246, 0.12)", color: "#2563eb" };
        case "Подготвя се":
        case "Подготвя се/Пикирана":
            return { bgcolor: "rgba(217, 119, 6, 0.14)", color: "#b45309" };
        case "Чекирана":
            return { bgcolor: "rgba(124, 58, 237, 0.12)", color: "#6d28d9" };
        case "Натоварена":
            return { bgcolor: "rgba(2, 132, 199, 0.12)", color: "#0369a1" };
        case "Изпратена":
            return { bgcolor: "rgba(2, 132, 199, 0.12)", color: "#0369a1" };
        case "Приключена днес":
        case "Доставена":
            return { bgcolor: "rgba(15, 118, 110, 0.12)", color: "#0f766e" };
        default:
            return { bgcolor: "rgba(100, 116, 139, 0.12)", color: "#475569" };
    }
}

export function getOrderStatusColumnStyles(status: string) {
    switch (status) {
        case "Нова":
            return {
                background:
                    "linear-gradient(180deg, rgba(239, 246, 255, 0.96), rgba(224, 236, 255, 0.84))",
                border: "1px solid rgba(59, 130, 246, 0.22)",
            };
        case "Подготвя се":
        case "Подготвя се/Пикирана":
            return {
                background:
                    "linear-gradient(180deg, rgba(255, 249, 240, 0.96), rgba(255, 244, 226, 0.84))",
                border: "1px solid rgba(217, 119, 6, 0.24)",
            };
        case "Чекирана":
            return {
                background:
                    "linear-gradient(180deg, rgba(245, 243, 255, 0.96), rgba(237, 233, 254, 0.84))",
                border: "1px solid rgba(124, 58, 237, 0.22)",
            };
        case "Натоварена":
        case "Изпратена":
            return {
                background:
                    "linear-gradient(180deg, rgba(240, 249, 255, 0.96), rgba(232, 246, 255, 0.84))",
                border: "1px solid rgba(2, 132, 199, 0.22)",
            };
        case "Приключена днес":
        case "Доставена":
            return {
                background:
                    "linear-gradient(180deg, rgba(240, 253, 250, 0.96), rgba(228, 250, 244, 0.84))",
                border: "1px solid rgba(15, 118, 110, 0.22)",
            };
        default:
            return {
                background:
                    "linear-gradient(180deg, rgba(250, 252, 255, 0.92), rgba(245, 248, 252, 0.82))",
                border: "1px solid rgba(203, 213, 225, 0.95)",
            };
    }
}

export function getOrderStatusColumnCardStyles(status: string) {
    switch (status) {
        case "Нова":
            return {
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                border: "1px solid rgba(59, 130, 246, 0.16)",
            };
        case "Подготвя се":
        case "Подготвя се/Пикирана":
            return {
                backgroundColor: "rgba(255, 251, 245, 0.94)",
                border: "1px solid rgba(217, 119, 6, 0.16)",
            };
        case "Чекирана":
            return {
                backgroundColor: "rgba(255, 255, 255, 0.92)",
                border: "1px solid rgba(124, 58, 237, 0.14)",
            };
        case "Натоварена":
        case "Изпратена":
            return {
                backgroundColor: "rgba(247, 252, 255, 0.94)",
                border: "1px solid rgba(2, 132, 199, 0.16)",
            };
        case "Приключена днес":
        case "Доставена":
            return {
                backgroundColor: "rgba(245, 253, 250, 0.94)",
                border: "1px solid rgba(15, 118, 110, 0.16)",
            };
        default:
            return {
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                border: "1px solid rgba(216, 225, 235, 0.95)",
            };
    }
}

export function getChannelChipStyles(channel: string) {
    switch (channel) {
        case "POS":
            return { bgcolor: "rgba(217, 119, 6, 0.14)", color: "#b45309" };
        case "WooCommerce":
            return { bgcolor: "rgba(59, 130, 246, 0.12)", color: "#2563eb" };
        default:
            return { bgcolor: "rgba(15, 118, 110, 0.12)", color: "#0f766e" };
    }
}

export function getloyaltyStatusChipStyles(status: string) {
    switch (status) {
        case "Дигитализирана":
        case "Активна":
            return { bgcolor: "rgba(15, 118, 110, 0.12)", color: "#0f766e" };
        case "Мигрирана":
            return { bgcolor: "rgba(2, 132, 199, 0.12)", color: "#0369a1" };
        case "Блокирана":
            return { bgcolor: "rgba(220, 38, 38, 0.12)", color: "#dc2626" };
        case "Неактивна":
            return { bgcolor: "rgba(71, 85, 105, 0.12)", color: "#475569" };
        default:
            return { bgcolor: "rgba(217, 119, 6, 0.14)", color: "#b45309" };
    }
}

export function getWorkflowStatusChipStyles(status: string) {
    switch (status) {
        case "В процес":
            return { bgcolor: "rgba(2, 132, 199, 0.12)", color: "#0369a1" };
        case "Планирано":
            return { bgcolor: "rgba(71, 85, 105, 0.12)", color: "#475569" };
        default:
            return { bgcolor: "rgba(217, 119, 6, 0.14)", color: "#b45309" };
    }
}

export function getB2BStatusChipStyles(status: string) {
    switch (status) {
        case "Активен":
            return { bgcolor: "rgba(15, 118, 110, 0.12)", color: "#0f766e" };
        case "Нов клиент":
            return { bgcolor: "rgba(2, 132, 199, 0.12)", color: "#0369a1" };
        default:
            return { bgcolor: "rgba(217, 119, 6, 0.14)", color: "#b45309" };
    }
}
